import { observable, action, makeObservable } from 'mobx';
import { RootStore } from 'stores';
import { IRegion } from 'types';
import { toast, toastTypeEnum } from 'common/utils';
import { getApiErrorMessage } from 'services/utils';
import {
  addRegionService,
  deleteRegionService,
  editRegionSerive,
  getRegionsService,
  IAddRegionData,
  IEditRegonData
} from 'services/region';

export type IInitialState = {
  data: {
    regions: IRegion[];
    editRegionData: IRegion | null;
  };
  ui: {
    getRegionsLoading: boolean;
    addRegionsLoading: boolean;
    editRegionLoading: string | null;
    deleteRegionLoading: string | null;
  };
};

export const initialState: IInitialState = {
  data: {
    regions: [],
    editRegionData: null
  },
  ui: {
    getRegionsLoading: false,
    addRegionsLoading: false,
    editRegionLoading: null,
    deleteRegionLoading: null
  }
};

export class RegionStore {
  state: IInitialState = initialState;
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      state: observable,
      getRegions: action,
      addRegion: action,
      deleteRegion: action,
      editRegion: action,
      setState: action,
      reset: action
    });

    this.rootStore = rootStore;
    /* use this space to hydrate state from localStorage */
  }

  getRegions = async () => {
    try {
      this.setState({ ui: { ...this.state.ui, getRegionsLoading: true } });
      const { data } = await getRegionsService();
      this.setState({ data: { ...this.state.data, regions: data } });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getRegionsLoading: false } });
    }
  };

  addRegion = async (params: IAddRegionData) => {
    try {
      this.setState({ ui: { ...this.state.ui, addRegionsLoading: true } });
      await addRegionService(params);
      await this.getRegions();
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, addRegionsLoading: false } });
    }
  };

  deleteRegion = async (params: string) => {
    try {
      this.setState({ ui: { ...this.state.ui, deleteRegionLoading: params } });
      await deleteRegionService(params);
      await this.getRegions();
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, deleteRegionLoading: null } });
    }
  };

  editRegion = async (params: IEditRegonData) => {
    try {
      this.setState({ ui: { ...this.state.ui, editRegionLoading: params.id } });
      await editRegionSerive(params);
      await this.getRegions();
      this.setState({ data: { ...this.state.data, editRegionData: null } });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, editRegionLoading: null } });
    }
  };

  setState = (params: Partial<IInitialState>) => {
    const { state } = this;
    this.state = {
      ...state,
      ...params
    };
    /* use this space to save/sync state with localStorage */
  };

  reset = () => {
    this.state = initialState;
    /* use this space to reset localStorage in case if you're persisting this state */
  };
}
